import { directoryClient } from 'shared/domain/directory/directoryClient';
import {
  listenToOpenCreateDirectoryForm,
  listenToOpenEditDirectoryForm,
  OpenCreateDirectoryOptions,
  OpenEditDirectoryEvent,
  isOpenEditDirectoryEvent,
} from 'presentation/directory/openDirectoryInput';
import {
  ComponentType,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCurrentUser } from 'store/user';
import { showErrorToastAndLog } from 'services/toaster';
import { useProject } from 'store/project';
import { userWithPermissionsInDtoToUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { nowISO } from 'shared/utils/date/dates';
import { useDocumentationStore } from 'store/documentation';
import {
  DirectoryFields,
  DirectoryFormContext,
  DirectoryInForm,
  SubmitForm,
} from './types';

interface DirectoryUploadResult {
  _id?: string;
}

function isDirectoryUploadResult(payload: unknown): payload is DirectoryUploadResult {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  const id = Reflect.get(payload, '_id');
  return id === undefined || typeof id === 'string';
}

const WithDirectoryFormContext = createContext<
  DirectoryFormContext | undefined
>(undefined);

const WithDirectoryForm: FC<{ children?: ReactNode }> = ({ children }) => {
  const currentUser = useCurrentUser();
  const { _id: projectId, organizationId } = useProject();

  const formName: 'directory' = 'directory';
  const SUBMIT_EVENT_TYPE = 'directory_submit_event';

  const [directory, setDirectory] = useState<DirectoryInForm | undefined>(
    undefined
  );
  const [createParentId, setCreateParentId] = useState<string | undefined>(
    undefined
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);

  const afterSubmitSuccess = useRef<CallableFunction | undefined>(
    undefined
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setDirectory(undefined);
    setCreateParentId(undefined);
    afterSubmitSuccess.current = undefined;
  }, []);

  const openDialog = useCallback((dir?: DirectoryInForm) => {
    setDirectory(dir);
    setIsOpen(true);
  }, []);

  const submitForm: SubmitForm = useCallback(
    async (values: DirectoryFields) => {
      setIsPosting(true);
      const author = userWithPermissionsInDtoToUserWithPersonalData(
        currentUser,
        organizationId,
        projectId
      );

      try {
        if (directory) {
          await directoryClient.edit(
            { ...values, localId: directory.localId, modifiedBy: author._id },
            directory._id || undefined
          );
        } else {
          const parentId =
            createParentId ??
            useDocumentationStore.getState().currentDirectory ??
            '';
          const uploaded = await directoryClient.create({
            ...values,
            parentId,
            createdBy: author,
            modifiedBy: author,
            createdAt: nowISO(),
            modifiedAt: nowISO(),
            deleted: false,
          });
          if (isDirectoryUploadResult(uploaded) && uploaded._id) {
            useDocumentationStore
              .getState()
              .markUploadedDirectoriesForHighlight([uploaded._id]);
          }
        }
      } catch (error) {
        showErrorToastAndLog('Error while updating a directory', error);
        afterSubmitSuccess.current = undefined;
        setIsPosting(false);
        return;
      }

      setIsPosting(false);
      setIsOpen(false);
      const afterSuccess = afterSubmitSuccess.current;
      afterSubmitSuccess.current = undefined;
      if (typeof afterSuccess === 'function') {
        afterSuccess();
      }
    },
    [currentUser, directory, createParentId, organizationId, projectId]
  );

  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const setAfterSubmitSuccess = useCallback(
    (action?: CallableFunction) => {
      afterSubmitSuccess.current =
        typeof action === 'function' ? action : undefined;
    },
    []
  );

  useEffect(() => {
    return listenToOpenEditDirectoryForm((event) => {
      const detail = event.detail;
      if (isOpenEditDirectoryEvent(detail)) {
        setCreateParentId(undefined);
        openDialog(detail);
      }
    });
  }, [openDialog]);

  useEffect(() => {
    return listenToOpenCreateDirectoryForm((event) => {
      const detail = event.detail;
      setCreateParentId(detail?.parentId);
      setAfterSubmitSuccess(detail?.onSubmitSuccess);
      openDialog(undefined);
    });
  }, [openDialog, setAfterSubmitSuccess]);

  const ctx = useMemo(
    () => ({
      directory,
      isOpen,
      closeDialog,
      openDialog,
      submitForm,
      isPosting,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      formName,
    }),
    [
      directory,
      isOpen,
      closeDialog,
      openDialog,
      submitForm,
      isPosting,
      releaseSubmitEvent,
    ]
  );

  return (
    <WithDirectoryFormContext.Provider value={ctx}>
      {children}
    </WithDirectoryFormContext.Provider>
  );
};

export function useDirectoryForm(): DirectoryFormContext {
  const context = useContext(WithDirectoryFormContext);
  if (context === undefined) {
    throw new Error(
      'useDirectoryForm must be used within an DirectoryFormContext'
    );
  }
  return context;
}

export const withDirectoryForm =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithDirectoryForm>
      <Component {...props} />
    </WithDirectoryForm>
  );
